import React, { useMemo, useState, useEffect } from 'react';
import Accordion from 'components/Accordion';
import DatePicker from 'components/DatePicker';
import TitleSemiBold from 'components/Typography/TitleSemiBold';
import useSelectedRounds from 'hooks/Meteorology/Fields/ModelPerformance/useSelectedRounds';
import usePeriodType from 'hooks/Meteorology/Fields/ModelPerformance/usePeriodType';
import useOptions from 'hooks/Meteorology/Fields/ModelPerformance/useOptions';
import { store } from 'redux/store';
import { setFilter } from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import { parse } from 'date-fns';

export default function RoundsSelector() {
  const { selectedRounds, setSelectedRounds } = useSelectedRounds();
  const { periodType } = usePeriodType();
  const { availableRounds, minDate, maxDate } = useOptions();

  const [localStartDate, setLocalStartDate] = useState<Date | null>(null);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(null);

  const minDateObj = useMemo(() => {
    if (!minDate) return undefined;
    return parse(minDate, 'yyyy-MM-dd', new Date());
  }, [minDate]);

  const maxDateObj = useMemo(() => {
    if (!maxDate) return undefined;
    return parse(maxDate, 'yyyy-MM-dd', new Date());
  }, [maxDate]);

  const isFree = periodType === 'free';
  const hasRounds = availableRounds != null && availableRounds.length > 0;

  useEffect(() => {
    if (periodType !== 'free') {
      setLocalStartDate(null);
      setLocalEndDate(null);
    }
  }, [periodType]);

  useEffect(() => {
    if (selectedRounds.length === 0) return;
    const times = selectedRounds.map((d) => d.getTime());
    const start = new Date(Math.min(...times));
    const end = new Date(Math.max(...times));
    setLocalStartDate(start);
    setLocalEndDate(end);
  }, [selectedRounds]);

  const startDate = useMemo(() => {
    if (localStartDate) return localStartDate;
    if (!selectedRounds.length) return null;
    return new Date(Math.min(...selectedRounds.map((d) => d.getTime())));
  }, [localStartDate, selectedRounds]);

  const endDate = useMemo(() => {
    if (localEndDate) return localEndDate;
    if (!selectedRounds.length) return null;
    return new Date(Math.max(...selectedRounds.map((d) => d.getTime())));
  }, [localEndDate, selectedRounds]);

  const handleDateChange = (dates: (Date | null)[] | Date | null) => {
    store.dispatch(setFilter({ analysisPeriod: undefined }));
    if (!Array.isArray(dates)) {
      setLocalStartDate(null);
      setLocalEndDate(null);
      setSelectedRounds([]);
      return;
    }
    const [newStartDate, newEndDate] = dates;
    setLocalStartDate(newStartDate);
    setLocalEndDate(newEndDate);

    if (newStartDate && newEndDate) {
      const startTime = newStartDate.getTime();
      const endTime = newEndDate.getTime();
      const inRange = (availableRounds ?? [])
        .map((roundStr) => parse(roundStr, 'yyyy-MM-dd', new Date()))
        .filter((d) => d.getTime() >= startTime && d.getTime() <= endTime);
      setSelectedRounds(inRange);
    } else {
      setSelectedRounds([]);
    }
  };

  if (!isFree) {
    return null;
  }

  return (
    <Accordion.Item eventKey="rounds-selector" id="rounds-selector-accordion">
      <Accordion.Header>
        <TitleSemiBold>Selecione as rodadas</TitleSemiBold>
      </Accordion.Header>
      <Accordion.Body>
        {!hasRounds && (
          <div className="text-muted text-center py-3">
            Selecione o tipo de análise e a variável primeiro
          </div>
        )}
        {hasRounds && (
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            minDate={minDateObj}
            maxDate={maxDateObj}
            inline
          />
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
}
